import { ApolloCache, DocumentNode, NormalizedCacheObject, Reference, TypedDocumentNode } from "@apollo/client";
import { FieldFunctionOptionsEx, OverlayContext } from './context'

interface Reducer<TData> {
  (arr: TData[]): TData[]
}

export interface Expression<TData, V> {
  (r: TData): V
}

const camelize = (str: string) => {
  return str.replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
    return index === 0 ? word.toLowerCase() : word.toUpperCase();
  }).replace(/\s+/g, '');
}

class FilteredList<TData = any, TVariables = any> {
  protected context: OverlayContext
  protected fragment: DocumentNode | TypedDocumentNode<TData, TVariables>
  protected fragmentName: string | undefined
  protected additions: string[]
  protected removals: Record<string, string | null | undefined>
  protected reducers: Reducer<TData>[]

  constructor(context: OverlayContext, fragment: DocumentNode | TypedDocumentNode<TData, TVariables>, fragmentName: string | undefined, additions: string[], removals: Record<string, string | null | undefined>, reducers: Reducer<TData>[]) {
    this.context = context;
    this.fragment = fragment;
    this.fragmentName = fragmentName;
    this.additions = additions;
    this.removals = removals;
    this.reducers = reducers;
  }

  isTrue(val: Expression<TData, boolean>) {
    const reducer = (arr: TData[]) => {
      return arr.filter(r => {
        return val(r) == true;
      })
    }

    return new FilteredList(this.context, this.fragment, this.fragmentName, this.additions, this.removals, this.reducers.concat(reducer));
  }

  listContains<V>(list: V[] | undefined | null, val: Expression<TData, V>) {
    if (!list || list.length == 0) {
      return this;
    }

    const reducer = (arr: TData[]) => {
      return arr.filter(r => {
        var v = val(r);
        if (!v) {
          return false;
        }
        return list!.indexOf(v) >= 0;
      })
    }

    return new FilteredList(this.context, this.fragment, this.fragmentName, this.additions, this.removals, this.reducers.concat(reducer));
  }

  textIsFoundIn(text: string | undefined | null, ...vals: Expression<TData, string | null | undefined>[]) {
    if (!text) {
      return this;
    }

    const reducer = (arr: TData[]) => {
      return arr.filter(r => {
        var match = false;

        for (var val of vals) {
          var v = val(r);
          if (!v) {
            continue;
          }
          if (v.indexOf(text!) >= 0) {
            match = true;
            break;
          }
        }

        return match;
      })
    }

    return new FilteredList(this.context, this.fragment, this.fragmentName, this.additions, this.removals, this.reducers.concat(reducer));
  }

  charSeparatedListContains(list: string | undefined | null, val: Expression<TData, string>, char: string = ',') {
    if (!list) {
      return this;
    }
    var parts = list.split(char);
    return this.listContains(parts, val);
  }

  orderBy(val: Expression<TData, any> | string, desc: boolean): OrderedList<TData, TVariables> {
    if (typeof val === 'string') {
      return this.orderBy((r: any) => {
        const propName = camelize(val);
        return r[propName];
      }, desc);
    }
    return new OrderedList(this.context, this.fragment, this.fragmentName, this.additions, this.removals, this.reducers, [[val, desc]]);
  }
}

class OrderedList<TData = any, TVariables = any> extends FilteredList {
  protected sorting: [Expression<TData, any>, boolean][]

  constructor(context: OverlayContext, fragment: DocumentNode | TypedDocumentNode<TData, TVariables>, fragmentName: string | undefined, additions: string[], removals: Record<string, string | null | undefined>, reducers: Reducer<TData>[], sorting: [Expression<TData, any>, boolean][]) {
    super(context, fragment, fragmentName, additions, removals, reducers);
    this.sorting = sorting;
  }

  override orderBy(val: Expression<TData, any>, desc: boolean) {
    return new OrderedList(this.context, this.fragment, this.fragmentName, this.additions, this.removals, this.reducers, this.sorting.concat([val, desc]));
  }

  protected compare(a: TData, b: TData) {
    for (var [val, desc] of this.sorting) {
      var va = val(a);
      var vb = val(b);
      if (va > vb) {
        return desc ? -1 : 1;
      }
      if (va < vb) {
        return desc ? 1 : -1;
      }
    }
    return 0;
  }

  protected mergeIncoming(cache: ApolloCache<NormalizedCacheObject>, incoming: Reference[], lastPage: boolean): Reference[]  {
    // Load and clean up additions
    var added: TData[] = [];
    for (let id of this.additions.slice()) {
      if (!(id in this.context._fragmentCache)) {
        const index = this.additions.indexOf(id);
        if (index >= 0) {
          this.additions.splice(index, 1);
        }
      }
      else {
        let data = cache.readFragment({ 
          id,
          fragment: this.fragment,
          fragmentName: this.fragmentName
        });
        if(!data) {
          data = this.context._fragmentCache[id].data;
          cache.writeFragment({ 
            id,
            data: data,
            fragment: this.fragment,
            fragmentName: this.fragmentName,
            broadcast: false
          })
        }
        if (data) {
          added.push(data);
        }
      }
    }

    // Filter and sort added items
    for (let reducer of this.reducers) {
      added = reducer(added);
    }
    added.sort((a, b) => this.compare(a,b));

    // Load incoming that have not been removed
    var merged: TData[] = [];
    for (let r of incoming) {
      if (this.additions.includes(r.__ref)) {
        continue;
      }

      const data = cache.readFragment({ fragment: this.fragment, id: r.__ref, returnPartialData: true });

      if (r.__ref in this.removals) {
        const newVersion = data["rowVersion"];
        const removedVersion = this.removals[r.__ref];

        if (newVersion && removedVersion && Number.parseInt(newVersion) > Number.parseInt(removedVersion)) {
          delete this.removals[r.__ref];
        }
        else {
          continue;
        }
      }

      if (data) {
        merged.push(data);
      }
    }

    // Merge incoming with added
    for (var i = 0; i < merged.length; i++) {
      while (added.length > 0 && this.compare(added[0], merged[i]) < 0) {
        merged.splice(i, 0, added[0]);
        added.splice(0, 1);
        ++i;
      }
    }

    // Append remaining items for the last page
    if (lastPage) {
      merged = merged.concat(added);
    }

    return merged.map(r => ({ __ref: cache.identify(r as any)! })).filter(r => r.__ref ? true : false);
  }

  merge(existing: Reference[], incoming: Reference[], options: FieldFunctionOptionsEx, lastPage: boolean) {
    this.context.mergeObjects(existing, incoming, options);
    incoming = this.mergeIncoming(options.cache, incoming, lastPage);

    const merged = existing ? existing.slice(0) : [];
    if (incoming) {
      const { args } = options;
      if (args) {
        const { offset = 0 } = args;

        for (let i = 0; i < incoming.length; ++i) {
          merged[offset + i] = incoming[i];
        }

        merged.splice(offset + incoming.length)
      } else {
        merged.push(...incoming);
      }
    }

    return merged;
  }
}

export class OverlayList<TData = any, TVariables = any> extends FilteredList<TData, TVariables> {
  constructor(context: OverlayContext, fragment: DocumentNode | TypedDocumentNode<TData, TVariables>, fragmentName: string | undefined = undefined) {
    super(context, fragment, fragmentName, [], {}, []);
  }

  /* @internal */
  add(id: string) {
    if (id in this.removals)
      delete this.removals[id];
    if (!this.additions.includes(id))
      this.additions.push(id);
  }

  /* @internal */
  remove(id: string, rowVersion: string | null | undefined) {
    const index = this.additions.indexOf(id);
    if (index >= 0)
      this.additions.splice(index, 1);
    this.removals[id] = rowVersion;
  }
}